import { memo, useState, useCallback } from "react";
import { cn } from "@repo/design-system/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, X, Tag } from "lucide-react";

export interface SellerPromoCodeProps {
  /** Cart ID for applying promo */
  cartId: string;
  /** Currently applied promo code */
  appliedCode?: string;
  /** Callback when promo is applied */
  onApply: (cartId: string, code: string) => Promise<void>;
  /** Callback when promo is removed */
  onRemove: (cartId: string) => Promise<void>;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Error message if code is invalid */
  error?: string;
  /**
   * Whether the entry field is shown. The owner decides when to open it (the
   * order summary opens it from an icon beside the amount). An error always
   * shows it. Defaults to shown, which is the cart seller-card behaviour.
   */
  expanded?: boolean;
  /**
   * Compact sizing for the order summary, and focus the field when it mounts.
   */
  compact?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * Per-seller promo code input with apply/remove functionality.
 * Each seller cart can have its own promo code.
 */
export const SellerPromoCode = memo(function SellerPromoCode({
  cartId,
  appliedCode,
  onApply,
  onRemove,
  disabled = false,
  error,
  expanded = true,
  compact = false,
  className
}: SellerPromoCodeProps) {
  const [code, setCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const isPending = isApplying || isRemoving;
  const showInput = expanded || !!error;

  const handleApply = useCallback(async () => {
    if (!code.trim() || isPending || disabled) return;

    setIsApplying(true);
    try {
      await onApply(cartId, code.trim());
      setCode(""); // Clear input on success
    } finally {
      setIsApplying(false);
    }
  }, [cartId, code, onApply, isPending, disabled]);

  const handleRemove = useCallback(async () => {
    if (isPending || disabled) return;

    setIsRemoving(true);
    try {
      await onRemove(cartId);
    } finally {
      setIsRemoving(false);
    }
  }, [cartId, onRemove, isPending, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleApply();
      }
    },
    [handleApply]
  );

  // If a code is already applied, show it with remove option
  if (appliedCode) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Tag className="h-4 w-4 text-green-600" aria-hidden="true" />
        <span className="flex-1 text-sm font-medium text-green-600">
          {appliedCode}
        </span>
        <button
          type="button"
          onClick={handleRemove}
          disabled={isPending || disabled}
          aria-label={`Remove promo code ${appliedCode}`}
          className={cn(
            "rounded p-1 text-gray-400 transition-colors",
            "hover:bg-gray-100 hover:text-gray-600",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
        >
          {isRemoving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  }

  if (!showInput) return null;

  return (
    <div className={cn("space-y-1", className)}>
      <div className={cn("flex gap-2", compact && "items-center")}>
        <Input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Promo code"
          disabled={isPending || disabled}
          autoFocus={compact}
          aria-label="Enter promo code"
          aria-invalid={!!error}
          aria-describedby={error ? `promo-error-${cartId}` : undefined}
          className={cn(
            "h-9 flex-1 text-sm",
            compact && "h-8 px-2.5 text-xs",
            error && "border-red-500 focus:border-red-500 focus:ring-red-200"
          )}
        />
        <Button
          type="button"
          variant={compact ? "ghost" : "secondary"}
          size="sm"
          onClick={handleApply}
          disabled={!code.trim() || isPending || disabled}
          className={cn("h-9 px-3", compact && "h-8 px-2 text-xs")}
        >
          {isApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
        </Button>
      </div>
      {error && (
        <p
          id={`promo-error-${cartId}`}
          className="text-xs text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
});
